import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MethodEditorDrawer } from './method-editor-drawer';
import { Zap, Plus, Trash2, Layers } from 'lucide-react';
import {
    extractPathParams,
    type MethodFormData,
    type HttpVerb,
    type RestMethodEntry,
} from '@/types/integration-form-types';

const VERB_COLORS: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-green-100 text-green-700',
    PUT: 'bg-amber-100 text-amber-700',
    PATCH: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
};

interface CanonicalSchemaRefs {
    input_schema_ref?: string | null;
    output_schema_ref?: string | null;
}

interface IntegrationMethodsTabProps {
    typeConfig: Record<string, unknown>;
    canonicalMethods?: Record<string, unknown>[];
    onSave: (updates: Record<string, unknown>) => void;
    isSaving?: boolean;
    appKey?: string;
    currentUserId?: string;
    currentOrgId?: string;
}

function schemaFieldValue(inline: Record<string, unknown> | undefined, ref: string | undefined, fallback: string): string {
    if (ref) return JSON.stringify({ $ref: ref }, null, 2);
    if (inline) return JSON.stringify(inline, null, 2);
    return fallback;
}

function buildSchemaRefLookup(canonicalMethods: Record<string, unknown>[] | undefined): Record<string, CanonicalSchemaRefs> {
    if (!canonicalMethods) return {};
    const lookup: Record<string, CanonicalSchemaRefs> = {};
    for (const m of canonicalMethods) {
        const name = m.name as string;
        if (!name) continue;
        const refs: CanonicalSchemaRefs = {};
        if (typeof m.input_schema_ref === 'string') refs.input_schema_ref = m.input_schema_ref;
        if (typeof m.inputSchemaRef === 'string') refs.input_schema_ref = m.inputSchemaRef as string;
        if (typeof m.output_schema_ref === 'string') refs.output_schema_ref = m.output_schema_ref;
        if (typeof m.outputSchemaRef === 'string') refs.output_schema_ref = m.outputSchemaRef as string;
        if (refs.input_schema_ref || refs.output_schema_ref) {
            lookup[name] = refs;
        }
    }
    return lookup;
}

function inlineRef(schema: Record<string, unknown> | undefined): string | undefined {
    if (schema && typeof schema.$ref === 'string') return schema.$ref;
    return undefined;
}

function methodMapToFormData(
    methodMap: Record<string, RestMethodEntry> | undefined,
    schemaRefLookup: Record<string, CanonicalSchemaRefs>,
): MethodFormData[] {
    if (!methodMap) return [];
    return Object.entries(methodMap).map(([name, entry]) => {
        const canonical = schemaRefLookup[name];
        const inputRef = entry.input_schema_ref ?? canonical?.input_schema_ref ?? inlineRef(entry.input_schema) ?? undefined;
        const outputRef = entry.output_schema_ref ?? canonical?.output_schema_ref ?? inlineRef(entry.output_schema) ?? undefined;
        return {
            name,
            description: entry.description ?? '',
            verb: (entry.verb?.toUpperCase() as HttpVerb) ?? 'GET',
            path: entry.path ?? '',
            semantics: 'query' as const,
            channels: ['sync'],
            pathParams: entry.path_params ?? extractPathParams(entry.path ?? ''),
            queryParams: (entry.query_params ?? []).map((k) => ({ key: k, valueExpression: `$.input.${k}` })),
            bodyParams: [],
            inputSchema: schemaFieldValue(entry.input_schema, inputRef, '{\n  "type": "object",\n  "properties": {}\n}'),
            outputSchema: schemaFieldValue(entry.output_schema, outputRef, '{\n  "type": "object"\n}'),
        };
    });
}

function formDataToMethodMap(methods: MethodFormData[]): Record<string, RestMethodEntry> {
    const map: Record<string, RestMethodEntry> = {};
    for (const m of methods) {
        let parsedInput: Record<string, unknown> | undefined;
        let parsedOutput: Record<string, unknown> | undefined;
        try { parsedInput = JSON.parse(m.inputSchema); } catch { /* skip */ }
        try { parsedOutput = JSON.parse(m.outputSchema); } catch { /* skip */ }

        const entry: RestMethodEntry = {
            verb: m.verb,
            path: m.path,
            description: m.description || undefined,
            path_params: m.pathParams.length > 0 ? m.pathParams : undefined,
            query_params: m.queryParams.length > 0 ? m.queryParams.map((q) => q.key) : undefined,
        };

        if (parsedInput && typeof parsedInput.$ref === 'string') {
            entry.input_schema_ref = parsedInput.$ref;
        } else {
            entry.input_schema = parsedInput;
        }
        if (parsedOutput && typeof parsedOutput.$ref === 'string') {
            entry.output_schema_ref = parsedOutput.$ref;
        } else {
            entry.output_schema = parsedOutput;
        }

        map[m.name] = entry;
    }
    return map;
}

function extractSchemaRef(schemaJson: string): string | null {
    try {
        const parsed = JSON.parse(schemaJson);
        if (parsed && typeof parsed.$ref === 'string') return parsed.$ref;
    } catch {
        const trimmed = schemaJson.trim();
        if (trimmed && !trimmed.startsWith('{') && trimmed.includes('/')) return trimmed;
    }
    return null;
}

function SchemaRefBadges({ inputSchema, outputSchema }: { inputSchema: string; outputSchema: string }) {
    const inputRef = extractSchemaRef(inputSchema);
    const outputRef = extractSchemaRef(outputSchema);
    if (!inputRef && !outputRef) return <span className="text-xs text-slate-400">—</span>;
    const sameRef = inputRef && outputRef && inputRef === outputRef;
    const shortName = (ref: string) => ref.split('/').pop() ?? ref;
    return (
        <div className="flex flex-col gap-1">
            {sameRef ? (
                <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 max-w-[180px]">
                    <Layers size={10} className="shrink-0" />
                    <span className="truncate">{shortName(inputRef!)}</span>
                </span>
            ) : (
                <>
                    {inputRef && (
                        <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 max-w-[180px]">
                            <Layers size={10} className="shrink-0" />
                            <span className="truncate">in: {shortName(inputRef)}</span>
                        </span>
                    )}
                    {outputRef && (
                        <span className="inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 max-w-[180px]">
                            <Layers size={10} className="shrink-0" />
                            <span className="truncate">out: {shortName(outputRef)}</span>
                        </span>
                    )}
                </>
            )}
        </div>
    );
}

export function IntegrationMethodsTab({ typeConfig, canonicalMethods, onSave, isSaving, appKey, currentUserId, currentOrgId }: IntegrationMethodsTabProps) {
    const methodMap = typeConfig.method_map as Record<string, RestMethodEntry> | undefined;
    const schemaRefLookup = useMemo(() => buildSchemaRefLookup(canonicalMethods), [canonicalMethods]);
    const methods = useMemo(() => methodMapToFormData(methodMap, schemaRefLookup), [methodMap, schemaRefLookup]);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState<MethodFormData | undefined>(undefined);
    const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

    const handleAdd = () => {
        setEditingMethod(undefined);
        setEditorOpen(true);
    };

    const handleEdit = (method: MethodFormData) => {
        setEditingMethod(method);
        setEditorOpen(true);
    };

    const handleSave = (saved: MethodFormData) => {
        let updated: MethodFormData[];
        if (editingMethod) {
            updated = methods.map((m) => (m.name === editingMethod.name ? saved : m));
        } else {
            updated = [...methods, saved];
        }
        onSave({
            type_config: {
                ...typeConfig,
                method_map: formDataToMethodMap(updated),
            },
        });
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        const updated = methods.filter((m) => m.name !== deleteTarget);
        onSave({
            type_config: {
                ...typeConfig,
                method_map: formDataToMethodMap(updated),
            },
        });
        setDeleteTarget(null);
    };

    const existingNames = methods
        .filter((m) => m.name !== editingMethod?.name)
        .map((m) => m.name);

    return (
        <div className="space-y-4">
            <Card className="border-border/50 p-6">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base">
                            Methods
                            <Badge variant="secondary" className="text-xs ml-2">{methods.length}</Badge>
                        </CardTitle>
                        <Button
                            size="sm"
                            onClick={handleAdd}
                            disabled={isSaving}
                            className="gap-1 bg-btn-primary text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Plus size={14} />
                            Add Method
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    {methods.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 py-8 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--list-controls-empty-search-chip-bg))]">
                                <Zap size={24} className="text-blue-400" />
                            </div>
                            <p className="text-sm text-slate-500">No methods defined.</p>
                        </div>
                    ) : (
                        <div className="rounded-md border border-border/50">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 border-b border-border/50">
                                        <TableHead className="text-xs w-20">Verb</TableHead>
                                        <TableHead className="text-xs">Name</TableHead>
                                        <TableHead className="text-xs">Path</TableHead>
                                        <TableHead className="text-xs max-w-[200px]">Description</TableHead>
                                        <TableHead className="text-xs">Schemas</TableHead>
                                        <TableHead className="text-xs w-20" />
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {methods.map((m) => (
                                        <TableRow
                                            key={m.name}
                                            className="cursor-pointer hover:bg-muted/50"
                                            onClick={() => handleEdit(m)}
                                        >
                                            <TableCell>
                                                <Badge
                                                    variant="secondary"
                                                    className={`text-[10px] font-mono ${VERB_COLORS[m.verb] ?? ''}`}
                                                >
                                                    {m.verb}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-mono text-sm font-medium">
                                                {m.name}
                                            </TableCell>
                                            <TableCell>
                                                <code className="text-xs font-mono text-slate-500">
                                                    {m.path}
                                                </code>
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">
                                                {m.description || '—'}
                                            </TableCell>
                                            <TableCell>
                                                <SchemaRefBadges inputSchema={m.inputSchema} outputSchema={m.outputSchema} />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteTarget(m.name);
                                                    }}
                                                    className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                                >
                                                    <Trash2 size={13} />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <MethodEditorDrawer
                open={editorOpen}
                onOpenChange={setEditorOpen}
                method={editingMethod}
                existingNames={existingNames}
                onSave={handleSave}
                appKey={appKey}
                currentUserId={currentUserId}
                currentOrgId={currentOrgId}
            />

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Method</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete the method <code className="font-mono">{deleteTarget}</code>?
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
